import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  fetchAudioBrandSafetySummary,
  fetchAudioContextualSummary,
  fetchAudioLinkedNamedEntitiesSummary,
  searchAudios,
} from '@services/index';
import {
  ContextualSummaryForEpisode,
  LinkedNamedEntitySummaryForEpisode,
} from '@insights_module/types/ContextualAndEntity';
import { useOverviewContext } from '@insights_module/context/OverviewContext';
import { IAB_CONTEXTUAL_CATEGORIES } from '@insights_module/constants/contextualAndEntity';
import { GarmOverviewRisks } from '@insights_module/types/BrandSafety';

export type RankedContextualSummary = ContextualSummaryForEpisode & {
  originalIndex: number;
  path: string[];
};
export type ContextualCategoriesTree = { [key: string]: ContextualCategoriesTree };
export type RankedNamedEntitySummary = LinkedNamedEntitySummaryForEpisode & {
  originalIndex: number;
  typeName: string;
};

export const useOverviewData = () => {
  const { t } = useTranslation();
  const {
    selectedDateRange,
    aqlQueries,
    trendingAqlQueries,
    episodesCount,
    trendingEpisodesCount,
    isEpisodeCountLoading,
    contextualSearchTerm,
    entitiesSearchTerm,
    selectedEntitiesType,
  } = useOverviewContext();

  ///////////////// Contextual Data /////////////////

  const { data: contextualSummaryForEpisode, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['contextual-overview', aqlQueries.contextualQuery],
    queryFn: () => fetchAudioContextualSummary({ query: aqlQueries.contextualQuery }),
    staleTime: 1000 * 60 * 30,
  });

  // we use originalIndex to keep track of the original ranking of the categories, even after filtering
  const rankedCategories: RankedContextualSummary[] = useMemo(() => {
    if (!contextualSummaryForEpisode) return [];

    const subcategories = [
      contextualSearchTerm,
      ...findAllSubcategoriesOfCategory(IAB_CONTEXTUAL_CATEGORIES, contextualSearchTerm),
    ];

    if (!contextualSearchTerm)
      return contextualSummaryForEpisode.map((item, index) => ({
        ...item,
        originalIndex: index,
        path: findParentCategoriesOfCategory(IAB_CONTEXTUAL_CATEGORIES, item.display_name) || [],
      }));

    return contextualSummaryForEpisode.reduce((acc, item, index) => {
      if (subcategories.includes(item.display_name)) {
        acc.push({
          ...item,
          originalIndex: index,
          path: findParentCategoriesOfCategory(IAB_CONTEXTUAL_CATEGORIES, item.display_name) || [],
        });
      }
      return acc;
    }, [] as RankedContextualSummary[]);
  }, [contextualSummaryForEpisode, contextualSearchTerm]);

  const top10Categories = rankedCategories?.slice(0, 10);

  ///////////////// Named Entities Data /////////////////

  const { data: namedEntitySummaryForEpisode, isLoading: isLoadingNamedEntities } = useQuery({
    queryKey: ['linked-named-entities-overview', aqlQueries.namedEntitiesQuery],
    queryFn: () => fetchAudioLinkedNamedEntitiesSummary({ query: aqlQueries.namedEntitiesQuery }),
    staleTime: 1000 * 60 * 30,
  });

  const filteredNamedEntities = useMemo(
    () =>
      namedEntitySummaryForEpisode?.linked_named_entities.filter(
        (entity) => !['CARDINAL', 'ORDINAL', 'DATE', 'TIME'].includes(entity.type)
      ),
    [namedEntitySummaryForEpisode]
  );

  // we use originalIndex to keep track of the original ranking of the categories, even after filtering
  const rankedNamedEntities: RankedNamedEntitySummary[] = useMemo(() => {
    if (!filteredNamedEntities) return [];

    return filteredNamedEntities.reduce((acc, item, index) => {
      // Check if the entity matches the search term (if specified)
      const matchesSearchTerm =
        !entitiesSearchTerm || item.value.toLowerCase().includes(entitiesSearchTerm.toLowerCase());

      // Check if the entity type matches the selected type (if specified)
      const matchesType = !selectedEntitiesType || item.type === selectedEntitiesType;

      // If both conditions are met, push to accumulator with additional properties
      if (matchesSearchTerm && matchesType) {
        acc.push({
          ...item,
          // we have legacy 'WORK' type that needs to be mapped to 'WORK_OF_ART'
          type: item.type === 'WORK' ? 'WORK_OF_ART' : item.type,
          originalIndex: index,
          typeName: t(`insights.overview_page.trending_section.top_entities.types.${item.type}`),
        });
      }

      return acc;
    }, [] as RankedNamedEntitySummary[]);
  }, [filteredNamedEntities, entitiesSearchTerm, selectedEntitiesType]);

  const top10NamedEntities = rankedNamedEntities?.slice(0, 10);

  //////////// Brand Safety Data ////////////

  const { data: brandSafetySummaryForEpisode, isLoading: isLoadingBrandSafety } = useQuery({
    queryKey: ['brand-safety-overview', aqlQueries.brandSafetyQuery],
    queryFn: () => fetchAudioBrandSafetySummary({ query: aqlQueries.brandSafetyQuery }),
    staleTime: 1000 * 60 * 30,
  });

  const { data: brandSafetyGarmRiskOverview } = useQuery({
    queryKey: ['brand-safety-garm-risk-overview', aqlQueries.brandSafetyGarmRiskOverviewQuery],
    queryFn: () =>
      searchAudios({
        page: 1,
        pageSize: 1,
        payload: { query: aqlQueries.brandSafetyGarmRiskOverviewQuery },
      }),
    staleTime: 1000 * 60 * 30,
  });

  const { data: brandSafetyTrendingGarmRiskOverview } = useQuery({
    queryKey: [
      'brand-safety-trending-garm-risk-overview',
      trendingAqlQueries.brandSafetyGarmRiskOverviewQuery,
    ],
    queryFn: () =>
      searchAudios({
        page: 1,
        pageSize: 1,
        payload: { query: trendingAqlQueries.brandSafetyGarmRiskOverviewQuery },
      }),
    staleTime: 1000 * 60 * 30,
    enabled: selectedDateRange.description !== 'all_time',
  });

  const garmPerRiskLevelOverview: GarmOverviewRisks = useMemo(() => {
    const summaryPerLevel = Object.entries(
      brandSafetySummaryForEpisode?.brand_safety_garm || {}
    ).reduce(
      (acc, cur) => {
        const [, risks] = cur;
        Object.entries(risks).forEach(([risk, count]) => {
          acc[risk as keyof GarmOverviewRisks] += count;
        });

        return acc;
      },
      {
        unrelated: 0,
        low: 0,
        medium: 0,
        high: 0,
        floor: 0,
      } as GarmOverviewRisks
    );

    const summary = Object.entries(summaryPerLevel).reduce((acc, cur) => {
      const [, value] = cur;
      return acc + value;
    }, 0);

    if (!summary) return summaryPerLevel;
    Object.entries(summaryPerLevel).forEach(([key, value]) => {
      summaryPerLevel[key as keyof GarmOverviewRisks] = (value / summary) * 100;
    });

    return summaryPerLevel;
  }, [brandSafetySummaryForEpisode?.brand_safety_garm]);

  return {
    top10Categories,
    rankedCategories,
    isLoadingCategories,
    top10NamedEntities,
    rankedNamedEntities,
    isLoadingNamedEntities,
    episodesCount,
    trendingEpisodesCount,
    isLoadingEpisodeCount: isEpisodeCountLoading,
    garmPerCategoryOverview: brandSafetySummaryForEpisode?.brand_safety_garm,
    garmPerRiskLevelOverview,
    isLoadingBrandSafety,
    totalRelatedGarmEpisodes: brandSafetyGarmRiskOverview?.pagination.total || 0,
    totalRelatedTrendingGarmEpisodes: brandSafetyTrendingGarmRiskOverview?.pagination.total || 0,
  };
};

function findAllSubcategoriesOfCategory(
  categories: ContextualCategoriesTree,
  category?: string
): string[] {
  const subcategories = [] as string[];

  if (!category) {
    for (const key in categories) {
      subcategories.push(key);
      subcategories.push(...findAllSubcategoriesOfCategory(categories, key));
    }
  } else {
    const subcategory = categories[category];
    if (subcategory) {
      for (const key in subcategory) {
        subcategories.push(key);
        subcategories.push(...findAllSubcategoriesOfCategory(categories, key));
      }
    }
  }

  return subcategories;
}

function findParentCategoriesOfCategory(
  categories: ContextualCategoriesTree,
  category: string,
  path: string[] = []
): string[] | null {
  for (const [key, value] of Object.entries(categories)) {
    if (key === category) {
      return [...path, key];
    }
    if (typeof value === 'object' && value !== null) {
      const result = findParentCategoriesOfCategory(value, category, [...path, key]);
      if (result) {
        return result;
      }
    }
  }
  return null;
}
